import { subDays } from 'date-fns';
import moment from 'moment';
import React, { useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import CustomDatePicker from '../../../../components/customDatePicker/CustomDatePicker';
import CustomDonutChart, {
  IDonutChartData,
} from '../../../../components/customDonutChart/CustomDonutChart';
import CustomRadio from '../../../../components/customRadio/CustomRadio';
import EmptyPlaceholder from '../../../../components/emptyPlaceholder/EmptyPlaceholder';
import UsageSummeryContainer from '../../../../components/usageSummeryContainer/UsageSummeryContainer';
import {
  IProtocolData,
  IProtocolUsage,
} from '../../../../interfaces/protocolData';
import { getProtocolData } from '../../../../services/dashboard';
import { chartColors, otherColors } from '../ProtocolReport';

interface IProps {
  broadbandOrCircuitId: string;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
}

const NoneBroadbandReport = (props: IProps) => {
  const { broadbandOrCircuitId, setLoading, setMessage } = props;

  const [fromDate, setFromDate] = useState<Date>(
    new Date(
      new Date().getFullYear(),
      new Date().getMonth() - 1,
      new Date().getDate()
    )
  );
  const [toDate, setToDate] = useState<Date>(new Date());
  const [usageData, setUsageData] = useState<IDonutChartData[]>([]);
  const [selectedRadioIndex, setSelectedRadioIndex] = useState(0);
  const [protocolData, setProtocolData] = useState<IProtocolData>();

  const handleSubmitData = () => {
    if (!broadbandOrCircuitId) {
      return;
    }

    setLoading(true);
    setMessage('');

    getProtocolData(
      broadbandOrCircuitId,
      moment(fromDate).format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
      moment(toDate).format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)
    )
      .then((data: IProtocolData) => {
        setProtocolData(data);
        if (data.protocolUsage.length <= 0) {
          throw new Error('No protocol data available');
        }

        setUsageData(transformData(0, data));
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setUsageData([]);
        setMessage(error.message);
      });
  };

  const calculateOtherDataUsage = (
    protocolUsage: IProtocolUsage[]
  ): IProtocolUsage => {
    const otherUsage: IProtocolUsage[] = protocolUsage.slice(10);
    return otherUsage.reduce(
      (currentValue, { totalReceivedBytes, totalTransmittedBytes, total }) => ({
        totalReceivedBytes:
          currentValue.totalReceivedBytes + totalReceivedBytes,
        totalTransmittedBytes:
          currentValue.totalTransmittedBytes + totalTransmittedBytes,
        total: currentValue.total + total,
        protocol: 'Others',
      }),
      {
        totalReceivedBytes: 0,
        totalTransmittedBytes: 0,
        total: 0,
        protocol: '',
      }
    );
  };

  const transformData = (
    index: number,
    data: IProtocolData
  ): IDonutChartData[] => {
    let key: keyof IProtocolUsage = 'total';
    if (index === 1) {
      key = 'totalReceivedBytes';
    } else if (index === 2) {
      key = 'totalTransmittedBytes';
    }

    const protocolUsage: IProtocolUsage[] = data.protocolUsage.sort(
      (a: IProtocolUsage, b: IProtocolUsage) => +b[key] - +a[key]
    );

    const otherDataUsageTotal: IProtocolUsage = calculateOtherDataUsage(
      data.protocolUsage
    );

    const modifiedProtocolUsage: IProtocolUsage[] = [
      ...protocolUsage.slice(0, 10),
      otherDataUsageTotal,
    ];

    const grandTotalBytes: IProtocolUsage = modifiedProtocolUsage.reduce(
      (currentValue, { totalReceivedBytes, totalTransmittedBytes, total }) => ({
        totalReceivedBytes:
          currentValue.totalReceivedBytes + totalReceivedBytes,
        totalTransmittedBytes:
          currentValue.totalTransmittedBytes + totalTransmittedBytes,
        total: currentValue.total + total,
        protocol: 'Others',
      }),
      {
        totalReceivedBytes: 0,
        totalTransmittedBytes: 0,
        total: 0,
        protocol: '',
      }
    );

    let randomColorIndex = 0;
    const usageWithColors: IDonutChartData[] = modifiedProtocolUsage.map(
      (item: IProtocolUsage) => {
        let color: string | undefined = chartColors.find(
          (platform) => platform.platform === item.protocol.toLowerCase()
        )?.color;
        if (!color) {
          color = otherColors[randomColorIndex];
          randomColorIndex++;
        }

        return {
          title: item.protocol,
          value: +((+item[key] / +grandTotalBytes[key]) * 100).toFixed(1),
          color,
        };
      }
    );

    return usageWithColors;
  };

  const radioIndexChangeHandler = (index: number) => {
    setSelectedRadioIndex(index);
    setUsageData([]);
    if (protocolData) {
      const transformedData = transformData(index, protocolData);
      setUsageData(transformedData);
    }
  };

  return (
    <>
      <Row className='row-cols-1 row-cols-md-2 gy-2 mt-2'>
        <Col>
          <CustomDatePicker
            startDate={fromDate}
            onChange={(value) => value && setFromDate(value)}
            textAlign='left'
            maxDate={new Date()}
            selected={fromDate}
            placeholderText='yyyy-mm-dd HH:mm'
            showTimeSelect
            dateFormat='yyyy-MM-dd HH:mm'
            selectsStart
            minDate={subDays(new Date(), 90)}
          />
        </Col>
        <Col>
          <CustomDatePicker
            onChange={(value) => value && setToDate(value)}
            textAlign='left'
            maxDate={new Date()}
            selected={toDate}
            placeholderText='yyyy-mm-dd HH:mm'
            dateFormat='yyyy-MM-dd HH:mm'
            selectsEnd
            minDate={fromDate}
            showTimeSelect
          />
        </Col>
      </Row>

      <Row className='mb-3 mt-3 justify-content-center'>
        <Col className='col-md-3'>
          <Button
            className='w-100'
            variant='success'
            onClick={handleSubmitData}
          >
            Submit
          </Button>
        </Col>
      </Row>

      {usageData.length > 0 ? (
        <UsageSummeryContainer
          title={`Protocol Report ${broadbandOrCircuitId}`}
        >
          <Row className='justify-content-center mt-4'>
            <Col className='col-md-10 col-12'>
              <CustomRadio
                selectedIndex={selectedRadioIndex}
                onChange={radioIndexChangeHandler}
                items={[
                  {
                    label: 'Total',
                    index: 0,
                  },
                  {
                    label: 'Download',
                    index: 1,
                  },
                  {
                    label: 'Upload',
                    index: 2,
                  },
                ]}
              />
              {usageData && <CustomDonutChart usageData={usageData} />}
            </Col>
          </Row>
        </UsageSummeryContainer>
      ) : (
        <Row className='justify-content-center'>
          <Col className='col-md-4 mt-4 d-none d-md-block'>
            <EmptyPlaceholder />
          </Col>
        </Row>
      )}
    </>
  );
};

export default NoneBroadbandReport;
